import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Copy as CopyIcon,
  Edit2 as EditIcon,
  Plus as PlusIcon,
  Search as SearchIcon,
} from 'react-feather';
import { toast } from 'react-toastify';

import {
  type AdminRole,
  type AdminTeamUser,
  createTeamUser,
  generateTeamPasswordResetLink,
  getApiErrorMessage,
  getCurrentAdminAccess,
  hasPermission,
  listRoles,
  listTeamUsers,
  refreshCurrentAdminToken,
  updateTeamUser,
} from '@/api/admin-access';
import Breadcrumb from '@/components/common/breadcrumb.component';
import { UserStore } from '@/stores';

interface TeamFormState {
  email: string;
  displayName: string;
  password: string;
  disabled: boolean;
  roleIds: number[];
}

const emptyForm: TeamFormState = {
  email: '',
  displayName: '',
  password: '',
  disabled: false,
  roleIds: [],
};

const UsersView = () => {
  const currentUser = UserStore.useState((state) => state.firebaseUser);
  const access = UserStore.useState((state) => state.access);
  const canWrite = hasPermission(access, 'settings', 'write');
  const [users, setUsers] = useState<AdminTeamUser[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminTeamUser | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<TeamFormState>(emptyForm);
  const [resetLink, setResetLink] = useState('');

  const sortedRoles = useMemo(
    () => [...roles].sort((a, b) => a.name.localeCompare(b.name)),
    [roles]
  );

  const loadUsers = useCallback(
    async (pageToken?: string, append = false) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const result = await listTeamUsers({
          q: query.trim() || undefined,
          limit: 100,
          pageToken,
        });
        setUsers((current) =>
          append ? [...current, ...result.users] : result.users
        );
        setNextPageToken(result.nextPageToken);
      } catch (error) {
        toast.error(getApiErrorMessage(error));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [query]
  );

  useEffect(() => {
    Promise.all([listRoles(), listTeamUsers({ limit: 100 })])
      .then(([roleResult, userResult]) => {
        setRoles(roleResult);
        setUsers(userResult.users);
        setNextPageToken(userResult.nextPageToken);
      })
      .catch((error) => toast.error(getApiErrorMessage(error)))
      .finally(() => setLoading(false));
  }, []);

  const openCreateDialog = () => {
    setSelectedUser(null);
    setForm(emptyForm);
    setResetLink('');
    setDialogOpen(true);
  };

  const openEditDialog = (user: AdminTeamUser) => {
    setSelectedUser(user);
    setForm({
      email: user.email || '',
      displayName: user.displayName || '',
      password: '',
      disabled: user.disabled,
      roleIds: user.roleIds,
    });
    setResetLink('');
    setDialogOpen(true);
  };

  const toggleRole = (roleId: number) => {
    setForm((current) => ({
      ...current,
      roleIds: current.roleIds.includes(roleId)
        ? current.roleIds.filter((id) => id !== roleId)
        : [...current.roleIds, roleId],
    }));
  };

  const refreshAccessIfCurrentUser = async (uid: string) => {
    if (currentUser?.uid !== uid) return;
    await refreshCurrentAdminToken();
    const currentAccess = await getCurrentAdminAccess();
    UserStore.update((state) => {
      state.access = currentAccess;
      state.accessError = null;
    });
  };

  const saveUser = async () => {
    if (!form.email.trim()) {
      toast.error('Email is required');
      return;
    }
    setSaving(true);
    try {
      if (selectedUser) {
        const updated = await updateTeamUser(selectedUser.uid, {
          email: form.email.trim(),
          displayName: form.displayName.trim(),
          ...(form.password ? { password: form.password } : {}),
          disabled: form.disabled,
          roleIds: form.roleIds,
        });
        setUsers((current) =>
          current.map((user) => (user.uid === updated.uid ? updated : user))
        );
        await refreshAccessIfCurrentUser(updated.uid);
        toast.success('Team member updated');
        setDialogOpen(false);
      } else {
        const result = await createTeamUser({
          email: form.email.trim(),
          displayName: form.displayName.trim(),
          ...(form.password ? { password: form.password } : {}),
          disabled: form.disabled,
          roleIds: form.roleIds,
        });
        setUsers((current) => [result.user, ...current]);
        setSelectedUser(result.user);
        setResetLink(result.passwordResetLink || '');
        toast.success('Team member created');
        if (!result.passwordResetLink) setDialogOpen(false);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const createResetLink = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      setResetLink(await generateTeamPasswordResetLink(selectedUser.uid));
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const copyResetLink = async () => {
    await navigator.clipboard.writeText(resetLink);
    toast.success('Password reset link copied');
  };

  let saveButtonLabel = 'Create user';
  if (selectedUser) saveButtonLabel = 'Save changes';
  if (saving) saveButtonLabel = 'Saving…';

  return (
    <div>
      <Breadcrumb title="Team" label="Team" parent="Settings" />

      <div className="container-fluid">
        <Box sx={{ maxWidth: 900, width: '100%', margin: '30px auto' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            mb={2}
          >
            <Box
              component="form"
              onSubmit={(event) => {
                event.preventDefault();
                loadUsers();
              }}
              sx={{ flex: 1, maxWidth: 500 }}
            >
              <TextField
                fullWidth
                size="small"
                label="Search team"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton type="submit" size="small">
                        <SearchIcon size={18} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {canWrite && (
              <Button
                variant="contained"
                startIcon={<PlusIcon size={17} />}
                onClick={openCreateDialog}
              >
                Add team member
              </Button>
            )}
          </Stack>

          <Card>
            {loading && (
              <Box py={6} textAlign="center">
                <CircularProgress size={28} />
              </Box>
            )}
            {!loading && users.length === 0 && (
              <Box p={4} textAlign="center">
                <Typography color="text.secondary">
                  No admin users found.
                </Typography>
              </Box>
            )}
            {!loading && users.length > 0 && (
              <List disablePadding>
                {[...users]
                  .sort((a, b) =>
                    (a.displayName || a.email || '').localeCompare(
                      b.displayName || b.email || ''
                    )
                  )
                  .map((user) => (
                    <ListItem
                      key={user.uid}
                      disablePadding
                      divider
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label={`Edit ${user.displayName || user.email}`}
                          onClick={() => openEditDialog(user)}
                        >
                          <EditIcon size={18} />
                        </IconButton>
                      }
                    >
                      <ListItemButton onClick={() => openEditDialog(user)}>
                        <ListItemAvatar>
                          <Avatar src={user.photoURL || undefined}>
                            {(user.displayName ||
                              user.email ||
                              '?')[0].toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <span>{user.displayName || user.email}</span>
                              {user.isOwner && (
                                <Chip size="small" label="Owner" />
                              )}
                              {user.isSuperuser && !user.isOwner && (
                                <Chip
                                  size="small"
                                  color="secondary"
                                  label="Superadmin"
                                />
                              )}
                              {user.disabled && (
                                <Chip
                                  size="small"
                                  color="error"
                                  label="Disabled"
                                />
                              )}
                            </Stack>
                          }
                          secondary={
                            <>
                              {user.displayName && user.email}
                              {user.roles.length > 0 && (
                                <span>
                                  {user.displayName && user.email ? ' · ' : ''}
                                  {user.roles
                                    .map((role) => role.name)
                                    .join(', ')}
                                </span>
                              )}
                            </>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
              </List>
            )}
          </Card>

          {nextPageToken && (
            <Box textAlign="center" mt={2}>
              <Button
                disabled={loadingMore}
                onClick={() => loadUsers(nextPageToken, true)}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </Box>
          )}
        </Box>
      </div>

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {selectedUser ? 'Edit team member' : 'Add team member'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              required
              label="Email"
              type="email"
              value={form.email}
              disabled={!canWrite || selectedUser?.isOwner}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  email: event.target.value,
                }))
              }
            />
            <TextField
              label="Display name"
              value={form.displayName}
              disabled={!canWrite}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  displayName: event.target.value,
                }))
              }
            />
            <TextField
              label={
                selectedUser ? 'New password (optional)' : 'Password (optional)'
              }
              type="password"
              value={form.password}
              disabled={!canWrite}
              helperText={
                selectedUser
                  ? 'Leave blank to keep the current password.'
                  : 'Leave blank to generate a password reset link.'
              }
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  password: event.target.value,
                }))
              }
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Roles
              </Typography>
              {sortedRoles.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No roles have been created yet.
                </Typography>
              ) : (
                <Stack>
                  {sortedRoles.map((role) => (
                    <FormControlLabel
                      key={role.role_id}
                      control={
                        <Checkbox
                          checked={form.roleIds.includes(role.role_id)}
                          disabled={!canWrite}
                          onChange={() => toggleRole(role.role_id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">{role.name}</Typography>
                          {role.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {role.description}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  ))}
                </Stack>
              )}
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.disabled}
                  disabled={!canWrite || selectedUser?.isOwner}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      disabled: event.target.checked,
                    }))
                  }
                />
              }
              label="Disable this admin account"
            />

            {resetLink && (
              <Alert severity="success">
                <Typography variant="body2" mb={1}>
                  Send this one-time password setup/reset link to the team
                  member.
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    fullWidth
                    size="small"
                    value={resetLink}
                    InputProps={{ readOnly: true }}
                  />
                  <IconButton
                    onClick={copyResetLink}
                    aria-label="Copy reset link"
                  >
                    <CopyIcon size={18} />
                  </IconButton>
                </Stack>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {selectedUser && canWrite && (
            <Button
              disabled={saving}
              onClick={createResetLink}
              sx={{ mr: 'auto' }}
            >
              Generate reset link
            </Button>
          )}
          <Button disabled={saving} onClick={() => setDialogOpen(false)}>
            Close
          </Button>
          {canWrite && (
            <Button disabled={saving} variant="contained" onClick={saveUser}>
              {saveButtonLabel}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UsersView;
